import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  getStockHistorySummary,
  getStockHistoryWinLoss,
  getStockHistoryDecay,
  getStockHistoryReturns,
  getStockHistoryStocks,
  getStockHistory,
  type StockHistorySummaryResponse,
  type StockHistoryWinLossResponse,
  type StockHistoryDecayResponse,
  type StockHistoryReturnsResponse,
  type StockHistoryStocksResponse,
  type StockHistoryResponse,
} from '../lib/api/stock-history';
import SummaryStrip from '../components/ForecastRecord/SummaryStrip';
import WinLossTable from '../components/ForecastRecord/WinLossTable';
import DecayAnalysis from '../components/ForecastRecord/DecayAnalysis';
import AverageReturnsTable from '../components/ForecastRecord/AverageReturnsTable';
import PerSymbolTable from '../components/ForecastRecord/PerSymbolTable';
import SymbolDrilldown from '../components/ForecastRecord/SymbolDrilldown';

// NOTE: This page is intentionally self-contained and uses only existing
// stock-history endpoints. Visuals are implemented with lightweight
// "micro charts" (CSS-based) to avoid new charting dependencies.

const HORIZONS = ['1d', '3d', '7d'] as const;
type Horizon = (typeof HORIZONS)[number];

export default function ForecastRecord() {
  const [summary, setSummary] = useState<StockHistorySummaryResponse | null>(null);
  const [winLoss, setWinLoss] = useState<StockHistoryWinLossResponse>([]);
  const [decay, setDecay] = useState<StockHistoryDecayResponse>([]);
  const [returns, setReturns] = useState<StockHistoryReturnsResponse>([]);
  const [stocks, setStocks] = useState<StockHistoryStocksResponse>([]);
  const [selectedHorizon, setSelectedHorizon] = useState<Horizon>('7d');
  const [selectedSymbol, setSelectedSymbol] = useState<string | 'ALL'>('ALL');
  const [symbolHistory, setSymbolHistory] = useState<StockHistoryResponse>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [symbolLoading, setSymbolLoading] = useState(false);
  const [symbolError, setSymbolError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedSymbol !== 'ALL') {
      loadSymbolHistory(selectedSymbol);
    } else {
      setSymbolHistory([]);
      setSymbolError(null);
    }
  }, [selectedSymbol]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, winLossRes, decayRes, returnsRes, stocksRes] = await Promise.all([
        getStockHistorySummary(),
        getStockHistoryWinLoss(),
        getStockHistoryDecay(),
        getStockHistoryReturns(),
        getStockHistoryStocks(),
      ]);

      setSummary(summaryRes);
      setWinLoss(winLossRes);
      setDecay(decayRes);
      setReturns(returnsRes);
      setStocks(stocksRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forecast record data');
    } finally {
      setLoading(false);
    }
  };

  const loadSymbolHistory = async (symbol: string) => {
    try {
      setSymbolLoading(true);
      setSymbolError(null);
      const history = await getStockHistory(symbol, 50);
      setSymbolHistory(history);
    } catch (err) {
      setSymbolError(err instanceof Error ? err.message : 'Failed to load symbol history');
      setSymbolHistory([]);
    } finally {
      setSymbolLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return dateStr; // ISO date string; avoid TZ shifts
  };

  const horizonLabel = useMemo(
    () => (selectedHorizon === '7d' ? '7-day' : selectedHorizon === '3d' ? '3-day' : '1-day'),
    [selectedHorizon],
  );

  const hasData = summary && summary.total_signals > 0;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="16rem">
        <Box textAlign="center">
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading forecast record data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="16rem">
        <Alert severity="error" sx={{ width: '100%', maxWidth: '400px' }}>
          Error: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header + Horizon Toggle */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: { md: 'space-between' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Forecast Record
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Institutional forecast audit: how DAC signals perform in live markets.
          </Typography>
          {summary && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.disabled' }}>
              Live tracking from {formatDate(summary.first_date)} to {formatDate(summary.last_date)}
            </Typography>
          )}
        </Box>

        <ButtonGroup variant="outlined" size="small" sx={{ bgcolor: 'background.default' }}>
          <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 1 }}>
            Horizon
          </Typography>
          {HORIZONS.map((h) => (
            <Button
              key={h}
              onClick={() => setSelectedHorizon(h)}
              variant={selectedHorizon === h ? 'contained' : 'outlined'}
              sx={{ minWidth: 'auto', px: 2, py: 1 }}
            >
              {h}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Summary Strip */}
      <SummaryStrip summary={summary} horizonLabel={horizonLabel} formatDate={formatDate} />

      {/* Empty state messaging for brand-new systems */}
      {!hasData && (
        <Alert
          severity="info"
          sx={{
            bgcolor: 'primary.dark',
            color: 'primary.light',
            '& .MuiAlert-icon': { color: 'primary.light' }
          }}
        >
          <Typography variant="caption">
            Forecast tracking is live. Metrics will populate automatically as real signals and outcomes accrue
            over time (1d/3d/7d windows).
          </Typography>
        </Alert>
      )}

      {/* Accuracy + Decay Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 3 }}>
        <WinLossTable winLoss={winLoss} />
        <DecayAnalysis decay={decay} />
      </Box>

      {/* Returns + Stock Leaderboard Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 3 }}>
        <AverageReturnsTable returns={returns} />
        <PerSymbolTable stocks={stocks} onSymbolClick={setSelectedSymbol} />
      </Box>

      {/* Per-Symbol Drilldown */}
      <SymbolDrilldown
        selectedSymbol={selectedSymbol}
        onSymbolChange={setSelectedSymbol}
        stocks={stocks}
        symbolHistory={symbolHistory}
        symbolLoading={symbolLoading}
        symbolError={symbolError}
        formatDate={formatDate}
      />
    </Box>
  );
}
